/**
 * Durable store for `pending_operator_steps`.
 *
 * WHY THIS EXISTS
 * ---------------
 * The lifecycle API returns the remaining HOST steps -- issue the
 * cert, regenerate the ACL, restart the bus, deploy/remove the worker
 * -- because it deliberately holds neither `docker.sock` nor the CA
 * key. Those steps are returned exactly once, in the response body.
 *
 * Left in React state, a page refresh, a closed tab, or a stray
 * navigation would discard them, and the two outcomes are not
 * symmetric:
 *
 *   - lost after COMMISSION: the site simply never comes up. Annoying,
 *     self-announcing, fixable by re-running the (idempotent) call.
 *   - lost after DECOMMISSION: the site is archived and gone from
 *     every listing, but the ACL was never regenerated, so **the
 *     retired site's certificate still has write access to the
 *     platform bus**. Nothing surfaces that. It is silent, and it is a
 *     security regression.
 *
 * So the checklist outlives the response. `localStorage`, not
 * `sessionStorage`: the steps are run in a terminal, quite possibly
 * after closing the browser, and the reminder needs to survive that.
 * Entries are cleared only by an explicit acknowledgement -- there is
 * no expiry, because "these steps stopped mattering after a while" is
 * exactly the assumption that would make this useless.
 *
 * This is a REMINDER, not a source of truth: it records what the API
 * said was outstanding, not what the operator actually ran. The
 * authoritative check is the Sites table's `lifecycle_state`, which
 * observes the bus. The two are complementary -- an acknowledged
 * checklist plus a `retired_still_publishing` row means someone
 * dismissed the reminder without running the steps.
 */
import type { LifecycleResponse } from '../api/adminClient';

const STORAGE_KEY = 'nexsoss:admin-console-pending-steps-v1';

/**
 * Same-tab change signal. The browser's own `storage` event only fires
 * in OTHER tabs, so a write here needs its own notification for the
 * banner to appear without a reload.
 *
 * Dispatched by the mutators below rather than by their callers: a
 * caller that forgot to notify would leave the checklist correctly
 * persisted but invisible until the next reload, which is the failure
 * this whole module exists to prevent, arrived at by a different route.
 */
export const PENDING_STEPS_EVENT = 'nexsoss:admin-pending-steps-changed';

function notifyChanged(): void {
  window.dispatchEvent(new CustomEvent(PENDING_STEPS_EVENT));
}

export interface PendingSteps {
  site_id: string;
  action: LifecycleResponse['action'];
  steps: string[];
  at_ms: number;
}

function read(): PendingSteps[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Shape-check every entry: this is parsed from storage a future
    // version may have written differently, and a malformed entry must
    // degrade to "no reminder" rather than crashing the shell that
    // renders it.
    return parsed.filter(
      (e): e is PendingSteps =>
        e &&
        typeof e.site_id === 'string' &&
        (e.action === 'commission' || e.action === 'decommission') &&
        Array.isArray(e.steps) &&
        typeof e.at_ms === 'number',
    );
  } catch {
    return [];
  }
}

function write(entries: PendingSteps[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Private-browsing / quota. The in-memory copy still renders for
    // this session; failing the whole lifecycle action over a storage
    // write would be worse than losing the reminder.
  }
}

export function listPendingSteps(): PendingSteps[] {
  return read().sort((a, b) => b.at_ms - a.at_ms);
}

export function recordPendingSteps(resp: LifecycleResponse): void {
  if (resp.pending_operator_steps.length === 0) return;
  // Keyed by site+action, so re-running an idempotent commission
  // replaces its own entry rather than stacking duplicates -- but a
  // decommission of the same site remains a SEPARATE entry, because
  // those checklists are different and both may be outstanding.
  const kept = read().filter((e) => !(e.site_id === resp.site_id && e.action === resp.action));
  kept.push({
    site_id: resp.site_id,
    action: resp.action,
    steps: resp.pending_operator_steps,
    at_ms: resp.at_ms,
  });
  write(kept);
  notifyChanged();
}

export function acknowledgePendingSteps(siteId: string, action: PendingSteps['action']): void {
  write(read().filter((e) => !(e.site_id === siteId && e.action === action)));
  notifyChanged();
}
